import { ReviewModalItem } from "@/components/display/ReviewModalItem";
import { useGetCurrentGroupIdQuery, useGetGroupsQuery, useGetToolUsageQuery } from "@/store/services/supabase";
import { AnalysisCollectionPermissions, GridItem } from "@/tools/aggregate/common/AnalysisCollectionFormItems";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { ArrowUpOutlined, ExclamationCircleFilled, PaperClipOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Progress, Statistic } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import type { SeveritiesAdvancedInputSettings } from "../types";
import { SeveritiesAdvancedSettings } from "./SeveritiesAdvancedSettings";
export interface SeveritiesReviewValues extends SeveritiesAdvancedInputSettings {
  name: string;
  isPrivate: boolean;
  groupId?: string;
}
interface SeveritiesUploadReviewProps {
  fileName: string;
  rowCount: number;
  onBack: () => void;
  onSubmit: (values: SeveritiesReviewValues) => void;
}
export const SeveritiesUploadReview = ({
  fileName,
  rowCount,
  onBack,
  onSubmit
}: SeveritiesUploadReviewProps) => {
  const {
    data: groups
  } = useGetGroupsQuery();
  const {
    data: currentGroupId
  } = useGetCurrentGroupIdQuery();
  const isOwner = useIsOwner();
  const [keys] = useKeys();
  const {
    t: terms
  } = useTranslation("terms");
  const [name, setName] = useState(removeFileExtension(fileName));
  // Without the org secret the run can't be shared, so it starts private.
  const [isPrivate, setIsPrivate] = useState(!keys.orgSecret);
  const [groupId, setGroupId] = useState("");
  const [settings, setSettings] = useState<SeveritiesAdvancedInputSettings>({
    anomalyManagementRp: true,
    defences: true
  });
  useEffect(() => {
    if (currentGroupId?.id) setGroupId(currentGroupId.id);
  }, [currentGroupId]);
  const groupOpts = groups?.map(g => ({
    key: g.id,
    value: g.id,
    label: g.name || ""
  })).filter(option => isOwner || option.key === groupId);
  return <div className="flex flex-col gap-4" data-sentry-component="SeveritiesUploadReview" data-sentry-source-file="SeveritiesUploadReview.tsx">
      <ReviewModalItem title="Review Upload" data-sentry-element="ReviewModalItem" data-sentry-source-file="SeveritiesUploadReview.tsx">
        <GridItem data-sentry-element="GridItem" data-sentry-source-file="SeveritiesUploadReview.tsx">
          <label htmlFor="severities-name">Name</label>
          <div className="col-span-2">
            <Input id="severities-name" className="h-7" value={name} onChange={e => setName(e.target.value)} prefix={<PaperClipOutlined />} data-sentry-element="Input" data-sentry-source-file="SeveritiesUploadReview.tsx" />
          </div>
        </GridItem>

        <GridItem data-sentry-element="GridItem" data-sentry-source-file="SeveritiesUploadReview.tsx">
          <div>Locations</div>
          <div className="col-span-2 text-sm">{rowCount.toLocaleString()}</div>
        </GridItem>

        <AnalysisCollectionPermissions isPrivate={isPrivate} setIsPrivate={setIsPrivate} enableShare={!!keys.orgSecret} groupOpts={groupOpts} groupId={groupId} setGroupId={setGroupId} data-sentry-element="AnalysisCollectionPermissions" data-sentry-source-file="SeveritiesUploadReview.tsx" />
      </ReviewModalItem>

      <SeveritiesAdvancedSettings data={settings} setData={setSettings} data-sentry-element="SeveritiesAdvancedSettings" data-sentry-source-file="SeveritiesUploadReview.tsx" />

      <Alert type="info" rootClassName="border-none text-sky-700" showIcon icon={<ExclamationCircleFilled width={12} />} className="border-none text-sky-700" description={<>
            <p>
              {terms("severities")} is under active development and the following hazards are in{" "}
              <span className="font-semibold">Beta</span>.
            </p>
            <ul className="list-inside list-disc">
              <li>{terms("hazards.surfacewaterFlooding")}</li>
              <li>{terms("hazards.riverineFlood")}</li>
            </ul>
          </>} data-sentry-element="Alert" data-sentry-source-file="SeveritiesUploadReview.tsx" />

      <SeveritiesUsageConfirmation rowCount={rowCount} isOwner={isOwner} onBack={onBack} onRun={() => onSubmit({
      name,
      isPrivate,
      groupId: isPrivate ? currentGroupId?.id : groupId,
      ...settings
    })} data-sentry-element="SeveritiesUsageConfirmation" data-sentry-source-file="SeveritiesUploadReview.tsx" />
    </div>;
};
const SeveritiesUsageConfirmation = ({
  rowCount,
  isOwner,
  onBack,
  onRun
}: {
  rowCount: number;
  isOwner: boolean;
  onBack: () => void;
  onRun: () => void;
}) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.severities.uploadLimitAlert"
  });
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  const usage = toolUsage?.severities;
  if (!usage) return null;
  const hasQuota = usage.quota > 0;
  const toPercent = (value: number) => hasQuota ? parseFloat((value / usage.quota * 100).toFixed(2)) : 0;
  const futureUsage = usage.usage + rowCount;
  const futureUsagePct = toPercent(futureUsage);
  const canRun = usage.alwaysEnabled || usage.enabled && hasQuota && futureUsagePct <= usage.hardLimitPercent;
  const role = isOwner ? "owner" : "user";
  return <>
      <ReviewModalItem title="Usage Confirmation" data-sentry-element="ReviewModalItem" data-sentry-source-file="SeveritiesUploadReview.tsx">
        <GridItem className="!items-start" data-sentry-element="GridItem" data-sentry-source-file="SeveritiesUploadReview.tsx">
          <div>Usage after upload</div>
          {hasQuota ? <div className="col-span-2 flex w-full flex-row justify-between gap-1 pr-2">
              <div className="flex flex-col gap-1">
                <Statistic value={`${futureUsage.toLocaleString()} / ${usage.quota.toLocaleString()} locations (${futureUsagePct}%)`} className="*:!text-sm" />
                <Statistic value={`${rowCount.toLocaleString()} (${toPercent(rowCount)}%)`} className="*:!text-sm *:!text-gray-400" suffix={<ArrowUpOutlined />} />
              </div>
              <Progress type="circle" percent={futureUsagePct} strokeWidth={16} size={42} success={{
            percent: usage.usagePercent,
            strokeColor: "#cdcdcd"
          }} strokeColor="var(--color-accent)" showInfo={false} className="max-w-8" />
            </div> : <div className="col-span-2 text-sm text-gray-500">No Severities allocation</div>}
        </GridItem>
      </ReviewModalItem>

      {hasQuota && futureUsagePct > 80 && futureUsagePct <= 100 && <Alert type="warning" showIcon message={<Markdown>{t(`${role}.warning`)}</Markdown>} />}
      {hasQuota && futureUsagePct > 100 && <Alert type="error" showIcon message={<Markdown>{t(`${role}.error`)}</Markdown>} />}
      {!hasQuota && !usage.alwaysEnabled && <Alert type="error" showIcon message={<Markdown>{t(`${role}.noAllocation`)}</Markdown>} />}

      <div className="flex justify-end gap-2">
        <Button type="text" onClick={onBack} data-sentry-element="Button" data-sentry-source-file="SeveritiesUploadReview.tsx">
          Back
        </Button>
        <Button type="primary" onClick={onRun} disabled={!canRun} data-sentry-element="Button" data-sentry-source-file="SeveritiesUploadReview.tsx">
          Start Run
        </Button>
      </div>
    </>;
};